import { useCallback, useState } from "react";

import { Alert } from "@vericus/cadmus-ui";

import { useAppSelector } from "@/data/hooks";
import { selectAnswerSnapshot } from "@/features/authority/authority-slice/selectors";
import { selectReattemptWindowEndDatePassed } from "@/features/socratic/socratic-slice";
import { attemptsRemaining } from "@/features/socratic/helpers/attempts-remaining";
import { useLatestDialogue } from "@/features/socratic/helpers/get-latest-dialogue";
import { hasCompletedResponse } from "@/features/socratic/helpers/has-dialogue-response";
import { selectedDialogueIdFromSnapshot } from "@/features/socratic/helpers/selected-dialogue-id";
import { useRecordingGate } from "@/features/socratic/hooks";
import { useCreateDialogue } from "@/features/socratic/hooks/use-create-dialogue";
import { useSelectVideoResponseAttempt } from "@/features/socratic/hooks/use-select-video-response-attempt";
import {
  SocraticDialogueFragment,
  SocraticSessionFragment,
} from "@/generated/graphql";
import { useRootNavigate } from "@/routes";

import { AttemptsList } from "./attempts-list";
import { ReattemptControls } from "./reattempt-controls";
import * as styles from "./socratic-session-entry.css";
import { StartDialogueCTA } from "./start-dialogue-cta";

interface Props {
  session: SocraticSessionFragment;
  answerBlockId: string;
  workId: string;
  isReadonly?: boolean;
  hasPreparationTime: boolean;
}

export function SocraticAttemptActions(props: Props) {
  const { session, answerBlockId, workId, isReadonly, hasPreparationTime } =
    props;
  const rootNavigate = useRootNavigate();

  const [isNavigating, setIsNavigating] = useState(false);

  const { createDialogue, creatingDialogue, createError, createMetadata } =
    useCreateDialogue();

  const handleStartOrReattemptPressed = () => {
    if (isReadonly === true) return;
    createDialogue({
      workId,
      session,
      onSuccess: () => {
        rootNavigate(`/work/${workId}/socratic/${answerBlockId}`);
        setIsNavigating(true);
      },
    });
  };

  const latestDialogue = useLatestDialogue(session.dialogues);

  const displayedNumAttemptsRemaining = attemptsRemaining({
    latestDialogue,
    maxAttempts: session.maxAttempts || 0,
    createMetadata,
  });

  const snapshot = useAppSelector((state) =>
    selectAnswerSnapshot(state, answerBlockId)
  );

  const reattemptWindowEndDatePassed = useAppSelector(
    selectReattemptWindowEndDatePassed
  );

  const { wouldAutoSubmitDuringRecording } = useRecordingGate();

  const selectedDialogueId = selectedDialogueIdFromSnapshot(snapshot);

  const selectAttempt = useSelectVideoResponseAttempt({
    answerBlock: session.answerBlock,
  });

  const handleSelectAttempt = useCallback(
    (dialogue: SocraticDialogueFragment) => {
      if (isReadonly === true) return;
      selectAttempt(dialogue);
    },
    [isReadonly, selectAttempt]
  );

  if (createError) {
    return (
      <Alert title="Something went wrong">
        Please reload the page to try again.
      </Alert>
    );
  }

  const loading = creatingDialogue || isNavigating;

  // Only show the submitted UI once a response has actually completed uploading.
  // A dialogue can exist without a completed response (abandoned / uploading /
  // failed), in which case the start CTA should still be shown.
  const hasResponse = hasCompletedResponse(session.dialogues);

  const hasReattemptsRemaining =
    displayedNumAttemptsRemaining > 0 &&
    reattemptWindowEndDatePassed !== true &&
    !wouldAutoSubmitDuringRecording;

  return (
    <div className={styles.actions}>
      {!hasResponse ? (
        <StartDialogueCTA
          onStartPressed={handleStartOrReattemptPressed}
          loading={loading}
          isReadonly={isReadonly}
          numAttemptsRemaining={displayedNumAttemptsRemaining}
          hasPreparationTime={hasPreparationTime}
        />
      ) : (
        <AttemptsList
          dialogues={session.dialogues}
          selectedDialogueId={selectedDialogueId}
          onSelectAttempt={handleSelectAttempt}
          hasReattemptsRemaining={hasReattemptsRemaining}
          isReadonly={isReadonly}
          headerAction={
            <ReattemptControls
              onReattemptPressed={handleStartOrReattemptPressed}
              loading={loading}
              isReadonly={isReadonly}
              numAttemptsRemaining={displayedNumAttemptsRemaining}
            />
          }
        />
      )}
    </div>
  );
}
